import React, { ReactNode, useRef, useState, useEffect, MouseEvent } from "react"
import { Menu, MenuItem, TableRow } from "@mui/material"
import styled from "styled-components"
import { theme } from "../../../../theme"
import { TBodyCell } from "../structures/TBodyCell"
import { FirstDegreeRowAction, RowAction } from "../types/RowAction"
import { ButtonText } from "../../../atoms/button"
import { ArrowRight, MoreVert } from "@mui/icons-material"
import { RowProps } from "../types/RowProps"

export type TBodyRowProps = {
  rowActions: FirstDegreeRowAction[]
  row: RowProps
  hover?: boolean
  children?: ReactNode
  className?: string
  onClick?: () => void
  onRowVisible?: () => void
  haveRowClickableChildren?: boolean
}

export const TBodyRow = (props: TBodyRowProps) => {
  const {
    rowActions,
    row,
    hover = true,
    children,
    className,
    onClick,
    onRowVisible,
    haveRowClickableChildren = false,
  } = props
  const clickable = haveRowClickableChildren || !!onClick

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<HTMLDivElement | null>(null)
  const openActionsMenu = !!actionsMenuAnchorEl

  const actionsMenuThreeDots = useRef<HTMLDivElement>(null)

  const [subActions, setSubActions] = useState<FirstDegreeRowAction[]>([])

  const [subActionsMenuAnchorEl, setSubActionsMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [currentlyHovering, setCurrentlyHovering] = useState({ hoveringState: false })

  const handleCloseActionsMenu = () => {
    setActionsMenuAnchorEl(null)
    setSubActionsMenuAnchorEl(null)
  }

  const handleCloseSubActionsMenu = () => {
    setSubActionsMenuAnchorEl(null)
  }

  const handleOpenActionsMenu = () => {
    setActionsMenuAnchorEl(actionsMenuThreeDots.current)
  }

  const handleHover = () => {
    setCurrentlyHovering({ hoveringState: true })
  }

  function handleCloseHover() {
    setCurrentlyHovering({ hoveringState: false })
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!currentlyHovering.hoveringState) {
        handleCloseSubActionsMenu()
      }
    }, 100)

    return () => {
      clearTimeout(timeout)
    }
  }, [currentlyHovering])

  const handleOpenSubActionsMenu = (e: MouseEvent, newSubActions: RowAction[]) => {
    setSubActionsMenuAnchorEl(e.currentTarget as HTMLElement)
    setSubActions(newSubActions)
  }

  const lastElementRef = useRef<HTMLTableRowElement>(null)

  const observer = new IntersectionObserver((observees) => {
    if (observees && observees[0].isIntersecting) {
      onRowVisible && onRowVisible()
    }
  })

  useEffect(() => {
    if (onRowVisible && lastElementRef.current) {
      observer.observe(lastElementRef.current)
    }
  }, [])

  return (
    <CustomRow
      className={className}
      hover={hover}
      onClick={onClick}
      $clickable={clickable}
      data-cy="tableRow"
      $openActionsMenu={openActionsMenu}
      ref={lastElementRef}
    >
      {children}
      {rowActions.length !== 0 && (
        <CustomTd className="sticky-row-actions">
          <Shadow></Shadow>
          <AnchorDiv ref={actionsMenuThreeDots}>
            {rowActions.length === 1 ? (
              <CustomButtonText
                icon={rowActions[0].icon ?? <MoreVert />}
                data-cy={rowActions[0].dataCy}
                onClick={(e) => {
                  e.stopPropagation()

                  if (rowActions[0].subActions && rowActions[0].subActions.length > 0) {
                    handleOpenActionsMenu()
                  } else {
                    !!rowActions[0].callback && rowActions[0]?.callback(row.uniqueIdentifier)
                  }
                }}
              />
            ) : (
              <CustomButtonText
                icon={<MoreVert />}
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenActionsMenu()
                }}
              />
            )}
          </AnchorDiv>

          <ActionsMenu
            open={openActionsMenu}
            onClose={handleCloseActionsMenu}
            anchorEl={actionsMenuAnchorEl}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            {/* Generate all first class actions */}
            {rowActions.map((rowAction, index) => {
              const haveSubActions = (rowAction.subActions?.length ?? 0) !== 0

              return (
                <MenuItem
                  key={`first-class-action-${index}`}
                  data-cy={rowAction.dataCy}
                  onClick={() => {
                    !haveSubActions && !!rowAction.callback && rowAction.callback(row.uniqueIdentifier)
                  }}
                  onMouseEnter={() => {
                    haveSubActions && handleHover()
                  }}
                  onMouseOver={(e) => {
                    haveSubActions && handleOpenSubActionsMenu(e, rowAction.subActions ?? [])
                  }}
                  onMouseLeave={(e) => {
                    haveSubActions && handleCloseHover()
                  }}
                  disableRipple={haveSubActions}
                >
                  <Action>
                    {rowAction.icon}
                    <div>
                      <ActionLabel>{rowAction.label}</ActionLabel>
                      <ActionDescription>{rowAction.description}</ActionDescription>
                    </div>

                    {haveSubActions && (
                      <ArrowRightDiv>
                        <ArrowRight />
                      </ArrowRightDiv>
                    )}
                  </Action>
                </MenuItem>
              )
            })}
          </ActionsMenu>

          <SubActionsMenu
            id="simple-menu"
            anchorEl={subActionsMenuAnchorEl}
            open={!!subActionsMenuAnchorEl}
            onClose={handleCloseSubActionsMenu}
            MenuListProps={{
              onMouseEnter: handleHover,
              onMouseLeave: handleCloseHover,
              style: { pointerEvents: "auto" },
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {subActions.map((subAction, index) => {
              return (
                <MenuItem
                  data-cy={subAction.dataCy}
                  onClick={() => {
                    !!subAction?.callback && subAction?.callback(row.uniqueIdentifier)
                    handleCloseSubActionsMenu()
                    handleCloseActionsMenu()
                  }}
                  key={`sub-action-${index}`}
                >
                  <Action>
                    {subAction.icon}
                    <div>
                      <ActionLabel>{subAction.label}</ActionLabel>
                      <ActionDescription>{subAction.description}</ActionDescription>
                    </div>
                  </Action>
                </MenuItem>
              )
            })}
          </SubActionsMenu>
        </CustomTd>
      )}
    </CustomRow>
  )
}

const CustomButtonText = styled(ButtonText)`
  padding: 16px;
  min-width: 72px;
  height: 100%;
  &:hover {
    background-color: ${theme.color.primary};
    color: ${theme.color.white};
  }
`

const ActionsMenu = styled(Menu)`
  .MuiMenu-paper {
    padding: 0 0 ${theme.spacing.m} 0;
    min-width: 212px;
  }

  .MuiMenu-list {
    padding: 0;
  }

  .MuiListItem-root {
    &.MuiListItem-root {
      & > * {
        padding: ${theme.spacing.m} ${theme.spacing.m};
      }
    }
  }

  .MuiListItem-root:hover {
    background-color: ${theme.color.grey5};
  }

  .MuiMenuItem-root {
    font-size: ${theme.fontSize.m};
    min-height: 28px;
    line-height: 28px;
  }

  .Mui-disabled {
    opacity: 1;
  }
`

const SubActionsMenu = styled(ActionsMenu)`
  // Necessary to keep sub menu open when we are still hovering on the first degree action
  &.MuiPopover-root {
    pointer-events: none;
  }

  margin-left: -2px;
`

const Action = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.m};
  align-items: center;
  width: 100%;
  min-height: 24px;

  svg {
    width: ${theme.iconSize.l};
    height: ${theme.iconSize.l};
    color: ${theme.color.grey1};
  }
`

const ActionLabel = styled.div`
  font-size: ${theme.fontSize.m};
  line-height: ${theme.lineHeight.m};
`

const ActionDescription = styled.div`
  font-size: ${theme.fontSize.s};
  line-height: ${theme.lineHeight.s};
  color: ${theme.color.grey1};
  max-width: 350px;
  white-space: pre-wrap;
`

const ArrowRightDiv = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;

  svg {
    color: ${theme.color.black5};
  }
`

const AnchorDiv = styled.div`
  position: absolute;
  top: 0;
  min-width: 72px;
  height: 100%;
`

const CustomRow = styled(TableRow)<{ $clickable: boolean; $openActionsMenu: boolean }>`
  cursor: ${(p) => (p.$clickable ? "pointer" : "auto")};
  position: ${(p) => (p.$clickable ? "relative" : "inherit")};

  && {
    &:hover {
      background-color: ${theme.color.primary50};
    }

    // Simulate that we are still hovering the row we are currently chosing actions for
    background-color: ${(p) => (p.$openActionsMenu ? theme.color.primary50 : "transparent")};
  }

  .sticky-row-actions {
    opacity: ${(p) => (p.$openActionsMenu ? 1 : 0)};
  }

  &:hover .sticky-row-actions {
    opacity: 1;
  }
`

const CustomTd = styled(TBodyCell)`
  right: 0;
  position: sticky;
  z-index: ${theme.zIndex.high};
  min-width: 72px;
  height: 72px;
  width: 72px;

  && {
    padding: 0;
  }

  background-color: ${theme.color.primary50};
`

const Shadow = styled.div`
  position: absolute;
  height: 100%;
  width: 6px;
  left: -6px;
  top: 0px;

  background: linear-gradient(270deg, rgba(113, 113, 113, 0.3) 0%, rgba(217, 217, 217, 0) 100%);
`
